// Tenant portal sidebar — 212px dark, matches the frameworks chrome.
//
// Per design/UI : UX v1.0/for-engineering-tenant-shell/specs/
// tenant-shell-strings.md § "Sidebar".
//
// Layout: brand block (wordmark + "TENANT PORTAL" sub + tenant card with
// jurisdiction flag) · "WORKSPACE" section label · 5 nav items (Coach
// topmost) · footer (EnvBadge + "EU · v0.5").
//
// RBAC: Adoption is tenant_admin-only; tenant_member won't see it (per
// navModel + visibleNav). Convenience filter; backend authorizes.

import { NavLink } from 'react-router-dom';
import { BellIcon, GlyphMark } from '@gc-coach/design-system';
import { isConsumerRoleKey } from '@gc-coach/shared';
import { useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import {
  AdoptionIcon,
  CoachIcon,
  DashboardIcon,
  FrameworksIcon,
} from './NavIcons.js';
import { EnvBadge, type EnvLabel } from './EnvBadge.js';
import { flagFromIso } from './flag.js';
import { visibleNav, type NavItem, type NavItemKey } from './navModel.js';
import styles from './TenantSidebar.module.css';

export interface TenantSidebarProps {
  /** Currently-active route key — drives the active visual state. */
  activeKey?: NavItemKey;
  /** Env footer label. */
  env: EnvLabel;
}

export function TenantSidebar({ activeKey, env }: TenantSidebarProps) {
  const { t } = useI18n();
  const { session } = useSession();

  // session.roles is wire-typed as (RoleKey | ConsumerRoleKey)[] across both
  // portals; narrow to ConsumerRoleKey defensively.
  const consumerRoles = (session?.roles ?? []).filter(isConsumerRoleKey);
  const items = visibleNav(consumerRoles);

  const tenantName = session?.tenant?.name ?? '';
  const jurisdictionIso = session?.tenant?.jurisdiction ?? '';
  const tenantDisplayName = tenantName.trim() || t('shell.tenantNameFallback');
  const jurisdictionFlag = jurisdictionIso ? flagFromIso(jurisdictionIso) : '';

  return (
    <aside className={styles.sidebar} aria-label={t('shell.sidebarLabel')}>
      <header className={styles.brand}>
        <div className={styles.brandRow}>
          <span className={styles.brandMark} aria-hidden="true">
            <GlyphMark width={22} height={22} />
          </span>
          <span className={styles.brandText}>
            <span className={styles.brandWordmark}>{t('shell.brandWordmark')}</span>
            <span className={styles.brandSub}>{t('shell.brandSub')}</span>
          </span>
        </div>

        <div className={styles.tenantCard}>
          <span className={styles.tenantFlag} aria-hidden="true">
            {jurisdictionFlag || '—'}
          </span>
          <span className={styles.tenantText}>
            <span className={styles.tenantName}>{tenantDisplayName}</span>
            <span className={styles.tenantLabel}>{t('shell.workspaceLabel')}</span>
          </span>
        </div>
      </header>

      <nav className={styles.nav} aria-label={t('shell.sidebarNavLabel')}>
        <span className={styles.sectionLabel}>{t('shell.workspaceLabel')}</span>
        <ul className={styles.navList}>
          {items.map((item) => (
            <li key={item.key}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  [
                    styles.navItem,
                    isActive || activeKey === item.key ? styles.navItemActive : null,
                  ]
                    .filter(Boolean)
                    .join(' ')
                }
                end={item.key === 'dashboard'}
              >
                <NavIcon itemKey={item.key} />
                <span className={styles.navLabel}>{t(`shell.nav.${item.key}`)}</span>
                <NavBadge item={item} />
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <footer className={styles.footer}>
        <EnvBadge env={env} trailing={t('shell.envTrailing')} />
      </footer>
    </aside>
  );
}

// ─── Icon dispatcher ─────────────────────────────────────────────────────────

function NavIcon({ itemKey }: { itemKey: NavItemKey }) {
  switch (itemKey) {
    case 'coach':
      return <CoachIcon className={styles.navIcon} />;
    case 'dashboard':
      return <DashboardIcon className={styles.navIcon} />;
    case 'frameworks':
      return <FrameworksIcon className={styles.navIcon} />;
    case 'notifications':
      return <BellIcon className={styles.navIcon} width={16} height={16} />;
    case 'adoption':
      return <AdoptionIcon className={styles.navIcon} />;
  }
}

// ─── Badge dispatcher ────────────────────────────────────────────────────────
// V1 placeholder — once Phase D wires notifications + framework counts, these
// will surface real numbers.

function NavBadge({ item }: { item: NavItem }) {
  switch (item.key) {
    case 'frameworks':
      // "{n} active" count pill — wired to real data in Phase D.
      // V1 placeholder hidden for empty-state demo.
      return null;
    case 'notifications':
      // Priority-colored unread badge — wired in Phase D.
      return null;
    default:
      return null;
  }
}
